import { useLingui } from "@lingui/react/macro";
import { Separator } from "@/components/ui/separator";
import { flagColorClass } from "@/lib/container-flags";
import type { LabelFieldKey } from "@/types/label-template";
import { labelFieldValue, type LabelFieldValues } from "./label-fields";
import { useLabelFieldNames } from "./useLabelFieldNames";

interface LabelPreviewProps {
  /** Resolved fields — tenant template with any recipe override applied. */
  fields: LabelFieldKey[];
  values: LabelFieldValues;
  title: string;
  /** Colours flagged on this recipe's containers, drawn beside the words (T-1005). */
  flagColors?: readonly string[];
}

/**
 * The sticker, as it will come out of the printer.
 *
 * It takes already-resolved fields rather than working them out, so the same
 * preview serves the tenant settings page and a recipe — and so a recipe's
 * preview changes the moment the tenant's template does (T-1001).
 */
export function LabelPreview({ fields, values, title, flagColors }: LabelPreviewProps) {
  const { t } = useLingui();
  const names = useLabelFieldNames();
  // The name is the heading; repeating it as a row would print it twice.
  const rows = fields.filter((f) => f !== "RECIPE_NAME");

  return (
    <div
      data-testid="label-preview"
      className="flex w-[280px] flex-col gap-4 rounded-[10px] border border-(--color-border) bg-(--color-surface-muted)/50 p-5"
    >
      <div className="flex flex-col gap-3">
        <p className="text-[14px] tracking-tight text-(--color-text-secondary)">
          {t`Example preview`}
        </p>
        <p className="text-center text-[20px] font-semibold tracking-tight">{title}</p>
      </div>

      <Separator />

      <div className="flex flex-col gap-4 text-[14px]">
        {rows.map((field) => (
          <div key={field} className="flex items-center justify-between gap-3">
            <span className="text-(--color-text-secondary)">{names[field]}:</span>
            <span className="flex min-w-0 items-center gap-1.5 font-medium">
              {field === "CONTAINER_FLAG" &&
                (flagColors ?? []).map((color) => (
                  <span
                    key={color}
                    data-testid={`preview-flag-${color}`}
                    className={`inline-block size-3 shrink-0 rounded-full ${flagColorClass(color)}`}
                    aria-hidden="true"
                  />
                ))}
              <span className="truncate">{labelFieldValue(field, values)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
